import { ReactElement, useState } from "react";
import { t } from "@lingui/core/macro";
import { Control, useForm } from "react-hook-form";

import { Uuid } from "_/types";
import { palette } from "_/style";

import {
  SsoProviderConfigView,
  useSsoProviders,
  useCreateSsoProvider,
  useCreateSsoProviderFromMetadata,
  useUpdateSsoProvider,
  useDeleteSsoProvider,
  useTestSsoProvider,
  NewSsoProviderOidc,
  NewSsoProviderSaml,
} from "_/data/sso";
import { Badge } from "_/components/badge";
import { Icon } from "_/components/icon";
import { Button } from "_/components/button";
import { Form } from "_/components/form";
import { Loader } from "_/components/loader";
import {
  Modal,
  SubmitCancelButtons,
  ButtonContainer,
} from "_/components/modal";
import { FormTextField } from "_/components/text-field";
import { Wizard, WizardStepDef } from "_/components/wizard";

import * as S from "./styled";

// Parse domains from a comma delimited string.
const parseDomains = (input: string): string[] =>
  input
    .split(",")
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);

type SsoProtocol = "oidc" | "saml2";
type SsoConfigMethod = "metadata" | "manual";
type SsoConfigStep = "protocol" | "config-method" | "fields" | "review";

type SsoConfigData = {
  protocol: SsoProtocol | null;
  configMethod: SsoConfigMethod | null;
  name: string;
  emailDomains: string;
  issuerUrl: string;
  clientId: string;
  clientSecret: string;
  idpEntityId: string;
  idpSsoUrl: string;
  idpCert: string;
  metadataUrl: string;
};

const INITIAL_WIZARD_DATA: SsoConfigData = {
  protocol: null,
  configMethod: null,
  name: "",
  emailDomains: "",
  issuerUrl: "",
  clientId: "",
  clientSecret: "",
  idpEntityId: "",
  idpSsoUrl: "",
  idpCert: "",
  metadataUrl: "",
};

const OidcLogo = () => {
  return (
    <svg
      width="4em"
      height="4em"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#9E9E9E"
        d="M44,27l-1-9l-2.9,1.9c-2.7-1.7-6.1-2.9-9.9-3.5c0,0-1.9-0.4-4.4-0.4s-4.8,0.3-4.8,0.3C11.3,17.5,4,23,4,29.6C4,36.4,11.5,42,23,43v-3.9c-7.9-1.1-12.9-4.8-12.9-9.5c0-4.4,4.6-8.1,10.9-9.3c0,0,4.9-1.1,9.2,0.2c2.1,0.5,4,1.2,5.6,2.2L32,25L44,27z"
      />
      <path d="M23 8L23 43 29 40 29 5z" />
      <path fill="#FF9800" d="M23 8L23 43 29 40 29 5z" />
    </svg>
  );
};

const SamlLogo = () => {
  return (
    <svg
      width="4em"
      height="4em"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g fill="#C22E33">
        <path d="M7.754 2l.463.41c.343.304.687.607 1.026.915C11.44 5.32 13.3 7.565 14.7 10.149c.072.132.137.268.202.403l.098.203-.108.057-.081-.115-.21-.299-.147-.214c-1.019-1.479-2.04-2.96-3.442-4.145a6.563 6.563 0 00-1.393-.904c-1.014-.485-1.916-.291-2.69.505-.736.757-1.118 1.697-1.463 2.653-.045.123-.092.245-.139.367l-.082.215-.172-.055c.1-.348.192-.698.284-1.049.21-.795.42-1.59.712-2.356.31-.816.702-1.603 1.093-2.39.169-.341.338-.682.5-1.025h.092z" />
        <path d="M8.448 11.822c-1.626.77-5.56 1.564-7.426 1.36C.717 11.576 3.71 4.05 5.18 2.91l-.095.218a4.638 4.638 0 01-.138.303l-.066.129c-.76 1.462-1.519 2.926-1.908 4.53a7.482 7.482 0 00-.228 1.689c-.01 1.34.824 2.252 2.217 2.309.67.027 1.347-.043 2.023-.114.294-.03.587-.061.88-.084.108-.008.214-.021.352-.039l.231-.028z" />
        <path d="M3.825 14.781c-.445.034-.89.068-1.333.108 4.097.39 8.03-.277 11.91-1.644-1.265-2.23-2.97-3.991-4.952-5.522.026.098.084.169.141.239l.048.06c.17.226.348.448.527.67.409.509.818 1.018 1.126 1.578.778 1.42.356 2.648-1.168 3.296-1.002.427-2.097.718-3.18.892-1.03.164-2.075.243-3.119.323z" />
      </g>
    </svg>
  );
};

const StepProtocol = ({
  data,
  onSelect,
}: {
  data: SsoConfigData;
  onSelect: (protocol: SsoProtocol) => void;
}): ReactElement => {
  return (
    <>
      <S.WizardOptionGroup>
        <S.WizardOptionCard
          type="button"
          $selected={data.protocol === "oidc"}
          onClick={() => onSelect("oidc")}
        >
          <OidcLogo />
          <S.WizardOptionHint>{t`components.sso-wizard.protocol-oidc`}</S.WizardOptionHint>
        </S.WizardOptionCard>
        <S.WizardOptionCard
          type="button"
          $selected={data.protocol === "saml2"}
          onClick={() => onSelect("saml2")}
        >
          <SamlLogo />
          <S.WizardOptionHint>{t`components.sso-wizard.protocol-saml2`}</S.WizardOptionHint>
        </S.WizardOptionCard>
      </S.WizardOptionGroup>
    </>
  );
};

const StepConfigMethod = ({
  data,
  onSelect,
}: {
  data: SsoConfigData;
  onSelect: (method: SsoConfigMethod) => void;
}): ReactElement => {
  return (
    <>
      <S.WizardStepLabel>
        {t`components.sso-wizard.saml-config-method-prompt`}
      </S.WizardStepLabel>
      <S.WizardOptionGroup>
        <S.WizardOptionCard
          type="button"
          $selected={data.configMethod === "metadata"}
          onClick={() => onSelect("metadata")}
        >
          {t`components.sso-wizard.config-method-automatic`}
          <S.WizardOptionHint>{t`components.sso-wizard.config-method-automatic-hint`}</S.WizardOptionHint>
        </S.WizardOptionCard>
        <S.WizardOptionCard
          type="button"
          $selected={data.configMethod === "manual"}
          onClick={() => onSelect("manual")}
        >
          {t`components.sso-wizard.config-method-manual`}
          <S.WizardOptionHint>{t`components.sso-wizard.config-method-manual-hint`}</S.WizardOptionHint>
        </S.WizardOptionCard>
      </S.WizardOptionGroup>
    </>
  );
};

const OidcFields = ({
  control,
}: {
  control: Control<SsoConfigData>;
}): ReactElement => {
  return (
    <S.WizardFields>
      <FormTextField
        control={control}
        name="name"
        label={t`components.sso-wizard.provider-name`}
        placeholder="e.g. Azure AD, Okta"
        rules={{ required: t`components.sso-wizard.name-required` }}
      />
      <FormTextField
        control={control}
        name="issuerUrl"
        label={t`components.sso-wizard.oidc-issuer-url`}
        rules={{ required: t`components.sso-wizard.oidc-issuer-url-required` }}
      />
      <FormTextField
        control={control}
        name="clientId"
        label={t`components.sso-wizard.oidc-client-id`}
        rules={{ required: t`components.sso-wizard.oidc-client-id-required` }}
      />
      <FormTextField
        control={control}
        name="clientSecret"
        label={t`components.sso-wizard.oidc-client-secret`}
        type="password"
        rules={{
          required: t`components.sso-wizard.oidc-client-secret-required`,
        }}
      />
      <FormTextField
        control={control}
        name="emailDomains"
        label={t`components.sso-wizard.email-domains`}
        placeholder="acme.com, acme.org"
      />
    </S.WizardFields>
  );
};

const SamlManualFields = ({
  control,
}: {
  control: Control<SsoConfigData>;
}): ReactElement => {
  return (
    <S.WizardFields>
      <FormTextField
        control={control}
        name="name"
        label={t`components.sso-wizard.provider-name`}
        placeholder="e.g. Okta SAML"
        rules={{ required: t`components.sso-wizard.name-required` }}
      />
      <FormTextField
        control={control}
        name="idpEntityId"
        label={t`components.sso-wizard.idp-entity-id`}
        rules={{ required: t`components.sso-wizard.idp-entity-id-required` }}
      />
      <FormTextField
        control={control}
        name="idpSsoUrl"
        label={t`components.sso-wizard.idp-sso-url`}
        placeholder="https://idp.example.com/sso"
        rules={{ required: t`components.sso-wizard.idp-sso-url-required` }}
      />
      <FormTextField
        control={control}
        name="idpCert"
        label={t`components.sso-wizard.idp-cert`}
        placeholder="-----BEGIN CERTIFICATE-----"
        rules={{ required: t`components.sso-wizard.idp-cert-required` }}
      />
      <FormTextField
        control={control}
        name="emailDomains"
        label={t`components.sso-wizard.email-domains`}
        placeholder="acme.com, acme.org"
      />
    </S.WizardFields>
  );
};

const SamlMetadataFields = ({
  control,
}: {
  control: Control<SsoConfigData>;
}): ReactElement => {
  return (
    <S.WizardFields>
      <FormTextField
        control={control}
        name="name"
        label={t`components.sso-wizard.provider-name`}
        placeholder="e.g. Okta SAML"
        rules={{ required: t`components.sso-wizard.name-required` }}
      />
      <FormTextField
        control={control}
        name="metadataUrl"
        label={t`components.sso-wizard.idp-metadata-url`}
        placeholder="https://idp.example.com/metadata"
        rules={{ required: t`components.sso-wizard.idp-metadata-url-required` }}
      />
      <FormTextField
        control={control}
        name="emailDomains"
        label={t`components.sso-wizard.email-domains`}
        placeholder="acme.com, acme.org"
      />
    </S.WizardFields>
  );
};

const StepFields = ({
  data,
  control,
}: {
  data: SsoConfigData;
  control: Control<SsoConfigData>;
}): ReactElement => {
  const isOidc = data.protocol === "oidc";
  const isSamlMetadata =
    data.protocol === "saml2" && data.configMethod === "metadata";

  if (isOidc) {
    return <OidcFields control={control} />;
  } else if (isSamlMetadata) {
    return <SamlMetadataFields control={control} />;
  } else {
    return <SamlManualFields control={control} />;
  }
};

const OidcReviewRows = ({ data }: { data: SsoConfigData }): ReactElement => {
  return (
    <>
      <dt>{t`components.sso-wizard.step-protocol`}</dt>
      <dd>OIDC</dd>
      <dt>{t`components.sso-wizard.provider-name`}</dt>
      <dd>{data.name}</dd>
      <dt>{t`components.sso-wizard.oidc-issuer-url`}</dt>
      <dd>{data.issuerUrl}</dd>
      <dt>{t`components.sso-wizard.oidc-client-id`}</dt>
      <dd>{data.clientId}</dd>
      <dt>{t`components.sso-wizard.oidc-client-secret`}</dt>
      <dd>••••••••</dd>
      <dt>{t`components.sso-wizard.email-domains`}</dt>
      <dd>{data.emailDomains || "—"}</dd>
    </>
  );
};

const SamlMetadataReviewRows = ({
  data,
}: {
  data: SsoConfigData;
}): ReactElement => {
  return (
    <>
      <dt>{t`components.sso-wizard.step-protocol`}</dt>
      <dd>SAML 2.0</dd>
      <dt>{t`common.configuration`}</dt>
      <dd>{t`components.sso-wizard.config-from-metadata`}</dd>
      <dt>{t`components.sso-wizard.provider-name`}</dt>
      <dd>{data.name}</dd>
      <dt>{t`components.sso-wizard.idp-metadata-url`}</dt>
      <dd>{data.metadataUrl}</dd>
      <dt>{t`components.sso-wizard.email-domains`}</dt>
      <dd>{data.emailDomains || "—"}</dd>
    </>
  );
};

const SamlManualReviewRows = ({
  data,
}: {
  data: SsoConfigData;
}): ReactElement => {
  return (
    <>
      <dt>{t`components.sso-wizard.step-protocol`}</dt>
      <dd>SAML 2.0</dd>
      <dt>{t`common.configuration`}</dt>
      <dd>{t`components.sso-wizard.config-method-manual`}</dd>
      <dt>{t`components.sso-wizard.provider-name`}</dt>
      <dd>{data.name}</dd>
      <dt>{t`components.sso-wizard.idp-entity-id`}</dt>
      <dd>{data.idpEntityId}</dd>
      <dt>{t`components.sso-wizard.idp-sso-url`}</dt>
      <dd>{data.idpSsoUrl}</dd>
      <dt>{t`components.sso-wizard.idp-cert`}</dt>
      <dd>{data.idpCert ? t`components.sso-wizard.cert-provided` : "—"}</dd>
      <dt>{t`components.sso-wizard.email-domains`}</dt>
      <dd>{data.emailDomains || "—"}</dd>
    </>
  );
};

const StepReview = ({ data }: { data: SsoConfigData }): ReactElement => {
  const isOidc = data.protocol === "oidc";
  const isSamlMetadata =
    data.protocol === "saml2" && data.configMethod === "metadata";

  let rows: ReactElement;
  if (isOidc) {
    rows = <OidcReviewRows data={data} />;
  } else if (isSamlMetadata) {
    rows = <SamlMetadataReviewRows data={data} />;
  } else {
    rows = <SamlManualReviewRows data={data} />;
  }

  return (
    <>
      <S.WizardStepLabel>{t`components.sso-wizard.review-prompt`}</S.WizardStepLabel>
      <S.WizardReview>{rows}</S.WizardReview>
    </>
  );
};

// Returns true when the required fields for the current path are all filled in.
const fieldsValid = (data: SsoConfigData): boolean => {
  if (data.protocol === "oidc") {
    return !!(
      data.name &&
      data.issuerUrl &&
      data.clientId &&
      data.clientSecret
    );
  }

  if (data.configMethod === "metadata") {
    return !!(data.name && data.metadataUrl);
  }

  // Manual SAML config
  return !!(data.name && data.idpEntityId && data.idpSsoUrl && data.idpCert);
};

/**
 * Builds the visible step list based on current wizard state. OIDC skips the
 * config-method step; SAML includes it. Before a protocol is chosen only the
 * protocol step is shown.
 */
const buildSteps = (
  data: SsoConfigData,
  control: Control<SsoConfigData>,
  selectProtocol: (p: SsoProtocol) => void,
  selectConfigMethod: (m: SsoConfigMethod) => void
): WizardStepDef<SsoConfigStep>[] => {
  const steps: WizardStepDef<SsoConfigStep>[] = [
    {
      key: "protocol",
      title: t`components.sso-wizard.step-protocol`,
      enumerated: false,
      content: <StepProtocol data={data} onSelect={selectProtocol} />,
    },
  ];

  if (data.protocol === "saml2") {
    steps.push({
      key: "config-method",
      title: t`components.sso-wizard.step-config-method`,
      enumerated: false,
      content: <StepConfigMethod data={data} onSelect={selectConfigMethod} />,
    });
  }

  if (data.protocol) {
    steps.push({
      key: "fields",
      title: t`common.details`,
      content: <StepFields data={data} control={control} />,
    });
    steps.push({
      key: "review",
      title: t`common.review`,
      content: <StepReview data={data} />,
    });
  }

  return steps;
};

const CreateSsoWizard = ({
  orgId,
  onClose,
}: {
  orgId: Uuid;
  onClose: () => void;
}): ReactElement => {
  const [step, setStep] = useState<SsoConfigStep>("protocol");

  const { control, watch, setValue } = useForm<SsoConfigData>({
    defaultValues: INITIAL_WIZARD_DATA,
    mode: "onChange",
  });

  const data = watch();

  const createProvider = useCreateSsoProvider();
  const createFromMetadata = useCreateSsoProviderFromMetadata();

  const isSubmitting = createProvider.isPending || createFromMetadata.isPending;

  const selectProtocol = (protocol: SsoProtocol) => {
    setValue("protocol", protocol);

    if (protocol === "saml2") {
      setStep("config-method");
    } else {
      setStep("fields");
    }
  };

  const selectConfigMethod = (configMethod: SsoConfigMethod) => {
    setValue("configMethod", configMethod);
    setStep("fields");
  };

  const steps = buildSteps(data, control, selectProtocol, selectConfigMethod);

  const canAdvance = step === "fields" ? fieldsValid(data) : false;

  const handleCreate = () => {
    if (data.protocol === "oidc") {
      const provider: NewSsoProviderOidc = {
        type: "oidc",
        name: data.name,
        issuerUrl: data.issuerUrl,
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        emailDomains: parseDomains(data.emailDomains),
      };
      createProvider.mutate({ orgId, provider }, { onSuccess: onClose });
      return;
    }

    if (data.configMethod === "metadata") {
      createFromMetadata.mutate(
        {
          orgId,
          name: data.name,
          metadataUrl: data.metadataUrl,
          emailDomains: parseDomains(data.emailDomains),
        },
        { onSuccess: onClose }
      );
      return;
    }

    const provider: NewSsoProviderSaml = {
      type: "saml2",
      name: data.name,
      idpEntityId: data.idpEntityId,
      idpSsoUrl: data.idpSsoUrl,
      idpCerts: [data.idpCert],
      emailDomains: parseDomains(data.emailDomains),
    };
    createProvider.mutate({ orgId, provider }, { onSuccess: onClose });
  };

  return (
    <Wizard
      title={t`components.sso-wizard.title`}
      onClose={onClose}
      steps={steps}
      step={step}
      onStepChange={setStep}
      canAdvance={canAdvance}
      onSubmit={handleCreate}
      submitLabel={t`components.sso-wizard.submit`}
      isSubmitting={isSubmitting}
    />
  );
};

type EditFormValues = {
  name: string;
  emailDomains: string;
  issuerUrl: string;
  clientId: string;
  clientSecret: string;
  idpEntityId: string;
  idpMetadataUrl: string;
  idpSsoUrl: string;
};

const ProviderCardEditForm = ({
  provider,
  onClose,
}: {
  provider: SsoProviderConfigView;
  onClose: () => void;
}): ReactElement => {
  const updateProvider = useUpdateSsoProvider();
  const isOidc = provider.providerType === "oidc";

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<EditFormValues>({
    defaultValues: {
      name: provider.name,
      emailDomains: provider.emailDomains.join(", "),
      issuerUrl: provider.oidc?.issuerUrl ?? "",
      clientId: provider.oidc?.clientId ?? "",
      clientSecret: "",
      idpEntityId: provider.saml?.idpEntityId ?? "",
      idpMetadataUrl: provider.saml?.idpMetadataUrl ?? "",
      idpSsoUrl: provider.saml?.idpSsoUrl ?? "",
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const oidc = isOidc
      ? {
          issuerUrl: data.issuerUrl,
          clientId: data.clientId,
          ...(data.clientSecret ? { clientSecret: data.clientSecret } : {}),
        }
      : undefined;

    const saml = !isOidc
      ? {
          idpEntityId: data.idpEntityId,
          idpMetadataUrl: data.idpMetadataUrl,
          idpSsoUrl: data.idpSsoUrl,
        }
      : undefined;

    await updateProvider.mutateAsync(
      {
        orgId: provider.organizationId,
        providerId: provider.id,
        update: {
          name: data.name,
          emailDomains: parseDomains(data.emailDomains),
          oidc,
          saml,
        },
      },
      { onSuccess: onClose }
    );
  });

  const oidcFields = (
    <>
      <FormTextField
        control={control}
        name="issuerUrl"
        label={t`components.sso-wizard.oidc-issuer-url`}
        rules={{ required: t`components.sso-wizard.oidc-issuer-url-required` }}
      />
      <FormTextField
        control={control}
        name="clientId"
        label={t`components.sso-wizard.oidc-client-id`}
        rules={{ required: t`components.sso-wizard.oidc-client-id-required` }}
      />
      <FormTextField
        control={control}
        name="clientSecret"
        label={t`components.sso-wizard.oidc-client-secret`}
        type="password"
        placeholder={t`components.sso-wizard.oidc-client-secret-placeholder`}
      />
    </>
  );

  const samlFields = (
    <>
      <FormTextField
        control={control}
        name="idpEntityId"
        label={t`components.sso-wizard.idp-entity-id`}
        rules={{ required: t`components.sso-wizard.idp-entity-id-required` }}
      />
      <FormTextField
        control={control}
        name="idpSsoUrl"
        label={t`components.sso-wizard.idp-sso-url`}
        rules={{ required: t`components.sso-wizard.idp-sso-url-required` }}
      />
      <FormTextField
        control={control}
        name="idpMetadataUrl"
        label={t`components.sso-wizard.idp-metadata-url`}
      />
    </>
  );

  const protocolFields = isOidc ? oidcFields : samlFields;

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="name"
        label={t`components.sso-wizard.provider-name`}
        rules={{ required: t`components.sso-wizard.name-required` }}
      />
      <FormTextField
        control={control}
        name="emailDomains"
        label={t`components.sso-wizard.email-domains`}
        placeholder="acme.com, acme.org"
      />
      {protocolFields}
      <SubmitCancelButtons
        onCancel={onClose}
        disableSubmit={!isDirty || updateProvider.isPending}
        submitButtonLabel={t`common.save`}
      />
    </Form>
  );
};

const OidcDetails = ({
  oidc,
}: {
  oidc: SsoProviderConfigView["oidc"];
}): ReactElement | null => {
  if (!oidc) return null;

  return (
    <S.ProviderCardDetails>
      <dt>{t`components.sso-wizard.oidc-issuer-url`}</dt>
      <dd>{oidc.issuerUrl}</dd>
      <dt>{t`components.sso-wizard.oidc-client-id`}</dt>
      <dd>{oidc.clientId}</dd>
    </S.ProviderCardDetails>
  );
};

const SamlDetails = ({
  saml,
  providerId,
}: {
  saml: SsoProviderConfigView["saml"];
  providerId: Uuid;
}): ReactElement | null => {
  if (!saml) return null;

  const baseUrl = `${window.location.origin}/api/v0/auth/sso/saml2`;
  const acsUrl = `${baseUrl}/acs/${providerId}`;
  const spEntityId = `${baseUrl}/metadata/${providerId}`;

  return (
    <S.ProviderCardDetails>
      <dt>{t`components.sso-wizard.sp-entity-id`}</dt>
      <dd>{spEntityId}</dd>
      <dt>{t`components.sso-wizard.sp-acs-url`}</dt>
      <dd>{acsUrl}</dd>
      <dt>{t`components.sso-wizard.idp-entity-id`}</dt>
      <dd>{saml.idpEntityId}</dd>
      <dt>{t`components.sso-wizard.idp-sso-url`}</dt>
      <dd>{saml.idpSsoUrl}</dd>
      <dt>{t`components.sso-wizard.certificates`}</dt>
      <dd>{saml.certCount}</dd>
    </S.ProviderCardDetails>
  );
};

// Card component for displaying an SSO config.
const ProviderCard = ({
  provider,
}: {
  provider: SsoProviderConfigView;
}): ReactElement => {
  const updateProvider = useUpdateSsoProvider();
  const deleteProvider = useDeleteSsoProvider();
  const testProvider = useTestSsoProvider();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const toggleEnabled = () => {
    updateProvider.mutate({
      orgId: provider.organizationId,
      providerId: provider.id,
      update: { isEnabled: !provider.isEnabled },
    });
  };

  const runTest = () => {
    testProvider.mutate({
      orgId: provider.organizationId,
      providerId: provider.id,
    });
  };

  const execDelete = () => {
    deleteProvider.mutate(
      { orgId: provider.organizationId, providerId: provider.id },
      { onSuccess: () => setConfirmDelete(false) }
    );
  };

  const typeLabel = provider.providerType === "oidc" ? "OIDC" : "SAML2";

  const testResult = !testProvider.isIdle && !testProvider.isPending && (
    <Icon
      style={{
        color: testProvider.isSuccess ? palette.kellyGreen : palette.tomatoRed,
      }}
      variant={testProvider.isSuccess ? "CheckCircle" : "CancelCircle"}
    />
  );

  return (
    <S.ProviderCard>
      <S.ProviderCardHeader>
        <S.ProviderCardHeaderLeft>
          <strong>{provider.name}</strong>
          <Badge kind="info">{typeLabel}</Badge>
          <Badge kind={provider.isEnabled ? "success" : "muted"}>
            {provider.isEnabled ? t`common.enabled` : t`common.disabled`}
          </Badge>
        </S.ProviderCardHeaderLeft>
        <S.ProviderCardActions>
          <Button
            kind="secondary"
            size="small"
            disabled={testProvider.isPending}
            onClick={runTest}
          >
            <span>{t`common.test`}</span>
            {testResult}
          </Button>
          <Button
            kind="secondary"
            size="small"
            disabled={updateProvider.isPending}
            onClick={toggleEnabled}
          >
            {provider.isEnabled ? t`common.disable` : t`common.enable`}
          </Button>
          <Button
            size="small"
            disabled={editing}
            onClick={() => setEditing(true)}
          >
            {t`common.edit`}
          </Button>
          <Button
            kind="danger"
            size="small"
            onClick={() => setConfirmDelete(true)}
          >
            {t`common.delete`}
          </Button>
        </S.ProviderCardActions>
      </S.ProviderCardHeader>

      {confirmDelete && (
        <Modal
          title={`${t`common.delete`} "${provider.name}"?`}
          open
          onClose={() => setConfirmDelete(false)}
        >
          <p>{t`components.sso-wizard.delete-confirmation ${typeLabel}`}</p>
          <ButtonContainer>
            <Button
              kind="secondary"
              size="small"
              onClick={() => setConfirmDelete(false)}
            >
              {t`common.cancel`}
            </Button>
            <Button
              kind="danger"
              size="small"
              onClick={execDelete}
              disabled={deleteProvider.isPending}
            >
              {deleteProvider.isPending
                ? `${t`common.deleting`}...`
                : t`common.delete`}
            </Button>
          </ButtonContainer>
        </Modal>
      )}

      {provider.providerType === "oidc" ? (
        <OidcDetails oidc={provider.oidc} />
      ) : (
        <SamlDetails saml={provider.saml} providerId={provider.id} />
      )}

      {editing && (
        <ProviderCardEditForm
          provider={provider}
          onClose={() => setEditing(false)}
        />
      )}
    </S.ProviderCard>
  );
};

export const SsoConfigSection = ({ orgId }: { orgId: Uuid }): ReactElement => {
  const { data: providers, isLoading } = useSsoProviders(orgId);
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <section>
      <h2>{t`components.sso-wizard.configurations`}</h2>

      {isLoading && <Loader loading />}

      {providers && providers.length === 0 && (
        <p style={{ marginBottom: "1rem" }}>
          {t`components.sso-wizard.no-providers`}
        </p>
      )}

      {providers && providers.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          {providers.map((p) => (
            <ProviderCard key={p.id} provider={p} />
          ))}
        </div>
      )}

      <Button size="small" onClick={() => setWizardOpen(true)}>
        {t`components.sso-wizard.add-provider`}
      </Button>

      {wizardOpen && (
        <CreateSsoWizard orgId={orgId} onClose={() => setWizardOpen(false)} />
      )}
    </section>
  );
};

export const SsoStatus = ({ orgId }: { orgId: Uuid }): ReactElement => {
  const { data: providers, isLoading } = useSsoProviders(orgId);

  if (isLoading) {
    return <Loader loading />;
  }

  const hasEnabled = providers?.some((p) => p.isEnabled) ?? false;

  return (
    <Icon
      style={{
        color: hasEnabled ? palette.kellyGreen : palette.tomatoRed,
        fontSize: "1.25em",
      }}
      variant={hasEnabled ? "CheckCircle" : "CancelCircle"}
    />
  );
};
